import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiCheck, FiUser, FiMapPin, FiShare2, FiCopy, FiTruck, FiClock } from 'react-icons/fi';
import { database } from '../config/firebase';
import { ref, push, serverTimestamp } from 'firebase/database';

// ─── Styled Components ──────────────────────────────────────────────────────

const WaitlistSection = styled.section`
  background: linear-gradient(135deg, #0a174e 0%, #13245c 60%, #1a0a2e 100%);
  position: relative;
  overflow: hidden;
  padding: 100px 20px;
`;

const BgGrid = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  z-index: 0;
`;

const GlowBlob = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(218,1,92,0.18) 0%, transparent 70%);
  border-radius: 50%;
  top: -100px;
  right: -100px;
  z-index: 0;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 580px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ff4d8f;
  margin-bottom: 16px;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 16px;
  span { color: #DA015C; }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.05rem;
  color: rgba(255,255,255,0.65);
  line-height: 1.75;
  margin-bottom: 40px;
  max-width: 480px;
`;

// Tab bar
const TabBar = styled.div`
  display: flex;
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 28px;
  border: 1px solid rgba(255,255,255,0.1);
`;

const TabBtn = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 14px 0;
  background: ${p => p.active ? '#DA015C' : 'rgba(255,255,255,0.05)'};
  color: ${p => p.active ? '#fff' : 'rgba(255,255,255,0.55)'};
  border: none;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.25s, color 0.25s;
  &:hover:not([disabled]) {
    background: ${p => p.active ? '#DA015C' : 'rgba(255,255,255,0.10)'};
    color: #fff;
  }
`;

// Card
const Card = styled(motion.div)`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 24px;
  padding: 40px 36px;
  width: 100%;
  backdrop-filter: blur(12px);
  text-align: left;
  @media (max-width: 600px) {
    padding: 28px 20px;
  }
`;

const StepIndicator = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 20px;
`;

const InputGroup = styled.div`
  margin-bottom: 18px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255,255,255,0.7);
  margin-bottom: 8px;
  svg { font-size: 0.9rem; color: #DA015C; }
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  font-size: 1rem;
  color: #fff;
  transition: border-color 0.2s, background 0.2s;
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: rgba(255,255,255,0.3); }
  &:focus {
    border-color: #DA015C;
    background: rgba(255,255,255,0.10);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  font-size: 1rem;
  color: #fff;
  outline: none;
  box-sizing: border-box;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s;
  &:focus { border-color: #DA015C; }
  option { background: #13245c; color: #fff; }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  input[type="radio"] { accent-color: #DA015C; width: 16px; height: 16px; }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const PrimaryBtn = styled(motion.button)`
  flex: 1;
  padding: 15px;
  background: #DA015C;
  color: #fff;
  font-weight: 800;
  font-size: 1rem;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, box-shadow 0.2s;
  &:hover:not(:disabled) {
    background: #b8004e;
    box-shadow: 0 8px 24px rgba(218,1,92,0.4);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SecondaryBtn = styled(motion.button)`
  flex: 1;
  padding: 15px;
  background: rgba(255,255,255,0.07);
  color: rgba(255,255,255,0.8);
  font-weight: 700;
  font-size: 1rem;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.12); }
`;

const ErrorMsg = styled.div`
  color: #ff6b8a;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 14px;
  padding: 10px 14px;
  background: rgba(218,1,92,0.12);
  border-radius: 8px;
  border-left: 3px solid #DA015C;
`;

const SuccessBox = styled(motion.div)`
  text-align: center;
  padding: 8px 0;
`;

const SuccessIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(218,1,92,0.15);
  border: 2px solid rgba(218,1,92,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px auto;
  svg { color: #DA015C; font-size: 1.8rem; }
`;

const SuccessTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 10px;
`;

const SuccessText = styled.p`
  font-size: 1rem;
  color: rgba(255,255,255,0.65);
  line-height: 1.7;
  margin-bottom: 24px;
`;

const ShareRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ShareBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.92rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
`;

const TrustRow = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 32px;
  justify-content: center;
  flex-wrap: wrap;
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
  font-weight: 600;
  svg { color: #DA015C; font-size: 0.85rem; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const DriverWaitlistEn: React.FC = () => {
  const [tab, setTab] = useState<'rider' | 'driver'>('rider');
  const [copied, setCopied] = useState(false);

  // ── Rider state
  const [riderData, setRiderData] = useState({ riderContact: '', riderCity: '' });
  const [riderSuccess, setRiderSuccess] = useState(false);
  const [riderError, setRiderError] = useState('');
  const [riderSubmitting, setRiderSubmitting] = useState(false);

  const handleRiderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRiderData(prev => ({ ...prev, [name]: value }));
  };

  const handleRiderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRiderError('');
    if (!riderData.riderContact || !riderData.riderCity) {
      setRiderError('Please fill in all required fields.');
      return;
    }
    setRiderSubmitting(true);
    try {
      await push(ref(database, 'riderApplications'), {
        riderContact: riderData.riderContact,
        riderCity: riderData.riderCity,
        timestamp: serverTimestamp(),
        submittedAt: new Date().toLocaleString('sv-SE', { timeZoneName: 'short' }),
      });
      setRiderSuccess(true);
      setRiderData({ riderContact: '', riderCity: '' });
    } catch {
      setRiderError('Something went wrong. Please try again.');
    } finally {
      setRiderSubmitting(false);
    }
  };

  // ── Driver state
  const [step, setStep] = useState(1);
  const [driverSuccess, setDriverSuccess] = useState(false);
  const [driverError, setDriverError] = useState('');
  const [driverSubmitting, setDriverSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    city: '',
    experience: '',
    vehicle: '',
    availability: '',
    fleet: '',
    hasLicense: '',
  });

  const handleDriverChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.phone) {
      setDriverError('Please fill in all required fields.');
      return;
    }
    setDriverError('');
    setStep(2);
  };

  const handleDriverSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.city || !formData.experience || !formData.vehicle || !formData.availability || !formData.fleet || !formData.hasLicense) {
      setDriverError('Please fill in all required fields.');
      return;
    }
    setDriverSubmitting(true);
    setDriverError('');
    try {
      await push(ref(database, 'driverApplications'), {
        ...formData,
        fleet: formData.fleet === 'yes',
        hasLicense: formData.hasLicense === 'yes',
        timestamp: serverTimestamp(),
        submittedAt: new Date().toLocaleString('sv-SE', { timeZoneName: 'short' }),
      });
      setDriverSuccess(true);
      setFormData({ fullName: '', phone: '', city: '', experience: '', vehicle: '', availability: '', fleet: '', hasLicense: '' });
    } catch {
      setDriverError('Something went wrong. Please try again.');
    } finally {
      setDriverSubmitting(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: 'Wassel', text: 'Join Wassel — rides coming to your city!', url: 'https://wassel.taxi' });
    } else {
      navigator.clipboard.writeText('https://wassel.taxi');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText('https://wassel.taxi');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const SuccessState = ({ title, text }: { title: string; text: string }) => (
    <SuccessBox initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
      <SuccessIconWrap><FiCheck /></SuccessIconWrap>
      <SuccessTitle>{title}</SuccessTitle>
      <SuccessText>{text}</SuccessText>
      <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', marginBottom: 16, fontWeight: 600 }}>
        Know someone who'd love Wassel? Share the link!
      </p>
      <ShareRow>
        <ShareBtn
          onClick={handleShare}
          style={{ background: '#DA015C', color: '#fff' }}
        >
          <FiShare2 /> Share with a Friend
        </ShareBtn>
        <ShareBtn
          onClick={handleCopyLink}
          style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
        >
          <FiCopy /> {copied ? 'Copied!' : 'Copy Link'}
        </ShareBtn>
      </ShareRow>
    </SuccessBox>
  );

  return (
    <WaitlistSection id="waitlist">
      <BgGrid />
      <GlowBlob />
      <Container>
        <Eyebrow>🚀 Coming Soon</Eyebrow>
        <Title
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          Join Wassel — <span>before launch</span>
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.1 }}
        >
          Whether you want to ride or drive, sign up now and get first access when we launch in your city.
        </Subtitle>

        <TabBar>
          <TabBtn active={tab === 'rider'} onClick={() => setTab('rider')}>
            <FiUser /> Join as Rider
          </TabBtn>
          <TabBtn active={tab === 'driver'} onClick={() => setTab('driver')}>
            <FiTruck /> Become a Driver
          </TabBtn>
        </TabBar>

        <AnimatePresence mode="wait">
          {tab === 'rider' ? (
            <Card
              key="rider-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              {riderSuccess ? (
                <SuccessState
                  title="You're on the list! 🎉"
                  text="We'll reach out as soon as Wassel launches in your area. Stay tuned — it's going to be worth the wait."
                />
              ) : (
                <form onSubmit={handleRiderSubmit}>
                  {riderError && <ErrorMsg>{riderError}</ErrorMsg>}
                  <InputGroup>
                    <Label htmlFor="riderContact"><FiUser /> Phone Number</Label>
                    <Input
                      id="riderContact"
                      name="riderContact"
                      type="tel"
                      value={riderData.riderContact}
                      onChange={handleRiderChange}
                      required
                      placeholder="+1 234 567 8900"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="riderCity"><FiMapPin /> Your City</Label>
                    <Input
                      id="riderCity"
                      name="riderCity"
                      type="text"
                      value={riderData.riderCity}
                      onChange={handleRiderChange}
                      required
                      placeholder="e.g. Jerusalem, Detroit, Marseille..."
                    />
                  </InputGroup>
                  <PrimaryBtn
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={riderSubmitting}
                    style={{ marginTop: 8, width: '100%' }}
                  >
                    {riderSubmitting ? 'Joining...' : <>Join Waitlist <FiArrowRight /></>}
                  </PrimaryBtn>
                </form>
              )}
            </Card>
          ) : (
            <Card
              key="driver-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              {driverSuccess ? (
                <SuccessState
                  title="Application received! 🚗"
                  text="Thanks for signing up as a driver. Our team will be in touch once we launch near you."
                />
              ) : (
                <form onSubmit={step === 1 ? handleContinue : handleDriverSubmit}>
                  <StepIndicator>Step {step} of 2</StepIndicator>
                  {driverError && <ErrorMsg>{driverError}</ErrorMsg>}

                  <AnimatePresence mode="wait">
                    {step === 1 ? (
                      <motion.div
                        key="driver-step1"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <InputGroup>
                          <Label htmlFor="fullName"><FiUser /> Full Name</Label>
                          <Input
                            id="fullName"
                            name="fullName"
                            type="text"
                            value={formData.fullName}
                            onChange={handleDriverChange}
                            required
                            placeholder="Your full name"
                          />
                        </InputGroup>
                        <InputGroup>
                          <Label htmlFor="phone"><FiUser /> Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleDriverChange}
                            required
                            placeholder="+1 234 567 8900"
                          />
                        </InputGroup>
                        <PrimaryBtn
                          type="submit"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          style={{ marginTop: 8, width: '100%' }}
                        >
                          Continue <FiArrowRight />
                        </PrimaryBtn>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="driver-step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <InputGroup>
                          <Label htmlFor="city"><FiMapPin /> Your City</Label>
                          <Input
                            id="city"
                            name="city"
                            type="text"
                            value={formData.city}
                            onChange={handleDriverChange}
                            required
                            placeholder="e.g. Jerusalem, Detroit, Marseille..."
                          />
                        </InputGroup>
                        <InputGroup>
                          <Label htmlFor="experience"><FiClock /> Driving Experience</Label>
                          <Select id="experience" name="experience" value={formData.experience} onChange={handleDriverChange} required>
                            <option value="">Select your experience</option>
                            <option value="Less than 1 year">Less than 1 year</option>
                            <option value="1-3 years">1–3 years</option>
                            <option value="3-5 years">3–5 years</option>
                            <option value="5+ years">5+ years</option>
                          </Select>
                        </InputGroup>
                        <InputGroup>
                          <Label htmlFor="vehicle"><FiTruck /> Vehicle Type</Label>
                          <Select id="vehicle" name="vehicle" value={formData.vehicle} onChange={handleDriverChange} required>
                            <option value="">Select vehicle type</option>
                            <option value="4-seater">4-Seater Car</option>
                            <option value="7-seater">7-Seater Van</option>
                            <option value="Minibus">Minibus (14–20 seats)</option>
                            <option value="Motorcycle">Motorcycle</option>
                            <option value="None (Will use company vehicle)">None (Will use company vehicle)</option>
                          </Select>
                        </InputGroup>
                        <InputGroup>
                          <Label><FiClock /> Availability</Label>
                          <Select id="availability" name="availability" value={formData.availability} onChange={handleDriverChange} required>
                            <option value="">Select availability</option>
                            <option value="Full-time">Full-time</option>
                            <option value="Part-time">Part-time</option>
                            <option value="Weekends only">Weekends only</option>
                            <option value="Evenings only">Evenings only</option>
                            <option value="Flexible">Flexible</option>
                          </Select>
                        </InputGroup>
                        <InputGroup>
                          <Label>Are you a fleet / taxi company?</Label>
                          <RadioGroup>
                            <RadioLabel>
                              <input type="radio" name="fleet" value="yes" checked={formData.fleet === 'yes'} onChange={handleDriverChange} required />
                              Yes, fleet/company
                            </RadioLabel>
                            <RadioLabel>
                              <input type="radio" name="fleet" value="no" checked={formData.fleet === 'no'} onChange={handleDriverChange} required />
                              No, individual driver
                            </RadioLabel>
                          </RadioGroup>
                        </InputGroup>
                        <InputGroup>
                          <Label>Do you have a valid taxi/driver license?</Label>
                          <RadioGroup>
                            <RadioLabel>
                              <input type="radio" name="hasLicense" value="yes" checked={formData.hasLicense === 'yes'} onChange={handleDriverChange} required />
                              Yes
                            </RadioLabel>
                            <RadioLabel>
                              <input type="radio" name="hasLicense" value="no" checked={formData.hasLicense === 'no'} onChange={handleDriverChange} required />
                              No
                            </RadioLabel>
                          </RadioGroup>
                        </InputGroup>
                        <ButtonRow>
                          <SecondaryBtn type="button" onClick={() => { setStep(1); setDriverError(''); }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            ← Back
                          </SecondaryBtn>
                          <PrimaryBtn type="submit" disabled={driverSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            {driverSubmitting ? 'Submitting...' : 'Submit Application'}
                          </PrimaryBtn>
                        </ButtonRow>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              )}
            </Card>
          )}
        </AnimatePresence>

        <TrustRow>
          <TrustItem><FiCheck /> No spam, ever</TrustItem>
          <TrustItem><FiCheck /> Free to join</TrustItem>
          <TrustItem><FiCheck /> Early access perks</TrustItem>
        </TrustRow>
      </Container>
    </WaitlistSection>
  );
};

export default DriverWaitlistEn;
